import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { LygoLogo } from '@/components/LygoLogo';

function useTokenFromQuery(): string {
  const location = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useTokenFromQuery();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError(t('auth.resetPassword.errorMissingToken'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth.resetPassword.errorMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.errorMismatch'));
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: password }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: t('auth.resetPassword.error') }));
        throw new Error(data.error || t('auth.resetPassword.error'));
      }

      setMessage(t('auth.resetPassword.success'));
      window.setTimeout(() => navigate('/login'), 1400);
    } catch (submitError) {
      const messageText = submitError instanceof Error ? submitError.message : t('auth.resetPassword.error');
      setError(messageText);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-8 bg-[radial-gradient(circle_at_15%_15%,hsl(193_62%_88%),transparent_34%),radial-gradient(circle_at_84%_88%,hsl(38_85%_90%),transparent_30%)]">
      <Card className="w-full max-w-[460px] border-primary/15 shadow-[0_14px_40px_rgba(15,23,42,0.12)]">
        <CardHeader className="space-y-1">
          <LygoLogo />
          <CardTitle className="text-2xl font-heading pt-1">{t('auth.brandName')}</CardTitle>
          <CardDescription>{t('auth.resetPassword.title')}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && (
            <Alert variant="success" className="mb-4">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.resetPassword.newPasswordLabel')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.resetPassword.confirmPasswordLabel')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">{t('auth.resetPassword.backToLogin')}</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
